const CLICK_DURATION = 600
const CLICK_RADIUS = 500
const CLICK_RESET_DELAY = 1000
const HOVER_IN_DURATION = 500
const HOVER_OUT_DURATION = 350
const HOVER_RADIUS = 60
const SELECTOR = '.btn--main, .btn--secondary, .btn--invert, .btn--invert-secondary'

interface ButtonState {
  mouseX: number
  mouseY: number
  radius: number
  rafId: number
}

class Button {
  private readonly prefersReducedMotion: boolean

  constructor() {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  public init(): void {
    if (this.prefersReducedMotion) return

    document.querySelectorAll<HTMLElement>(SELECTOR).forEach((element: HTMLElement): void => {
      this.setup(element)
    })
  }

  private addEvents(element: HTMLElement, overlay: HTMLElement, state: ButtonState): void {
    element.addEventListener('click', (e: MouseEvent): void => {
      if (e.clientX === 0 && e.clientY === 0) {
        const rect = element.getBoundingClientRect()
        state.mouseX = rect.width / 2
        state.mouseY = rect.height / 2
      } else {
        this.setMousePosition(element, e, state)
      }

      this.animateRadius(overlay, state, CLICK_RADIUS, CLICK_DURATION)
      setTimeout((): void => {
        state.radius = 0
        this.updateClipPath(overlay, state)
      }, CLICK_RESET_DELAY)
    })

    element.addEventListener('mouseenter', (e: MouseEvent): void => {
      this.setMousePosition(element, e, state)
      this.animateRadius(overlay, state, HOVER_RADIUS, HOVER_IN_DURATION)
    })

    element.addEventListener('mouseleave', (): void => {
      this.animateRadius(overlay, state, 0, HOVER_OUT_DURATION)
    })

    element.addEventListener('mousemove', (e: MouseEvent): void => {
      this.setMousePosition(element, e, state)
      this.updateClipPath(overlay, state)
    })
  }

  private animateRadius(
    overlay: HTMLElement,
    state: ButtonState,
    targetRadius: number,
    duration: number,
  ): void {
    const startRadius = state.radius
    const startTime = performance.now()

    cancelAnimationFrame(state.rafId)

    const tick = (now: number): void => {
      const elapsed = Math.min((now - startTime) / duration, 1)
      const progress = 1 - Math.pow(1 - elapsed, 3)

      state.radius = startRadius + (targetRadius - startRadius) * progress
      this.updateClipPath(overlay, state)

      if (elapsed < 1) {
        state.rafId = requestAnimationFrame(tick)
      }
    }

    state.rafId = requestAnimationFrame(tick)
  }

  private setMousePosition(element: HTMLElement, e: MouseEvent, state: ButtonState): void {
    const rect = element.getBoundingClientRect()
    state.mouseX = e.clientX - rect.left
    state.mouseY = e.clientY - rect.top
  }

  private setup(element: HTMLElement): void {
    if (element.querySelector('.btn__overlay') !== null) return

    const overlay = document.createElement('span')
    overlay.className = 'btn__overlay'
    overlay.setAttribute('aria-hidden', 'true')
    overlay.innerHTML = element.innerHTML
    element.appendChild(overlay)

    const state: ButtonState = { mouseX: 0, mouseY: 0, radius: 0, rafId: 0 }
    this.addEvents(element, overlay, state)
  }

  private updateClipPath(overlay: HTMLElement, state: ButtonState): void {
    overlay.style.clipPath = `circle(${state.radius}px at ${state.mouseX}px ${state.mouseY}px)`
  }
}

const button = new Button()
button.init()
